import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { store } from "react-notifications-component";
import PageHeader from "../../components/PageHeader";
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import {
    createCampaign,
    fetchCampaignTemplates,
    createCampaignTemplate,
} from "../../store/campaignStore/campaignActions";
import campaignApi from "../../store/campaignStore/campaignApi";
import { PENDING } from "../../store/globalStatuses/globalStatuses";
import styles from "./campaigns.module.scss";

const RETURN_WINDOW_OPTIONS = [7, 14, 30];
const VARIABLE_RE = /\{\{(\d+)\}\}/g;

const parseVariableIndices = (body) => {
    const found = new Set();
    VARIABLE_RE.lastIndex = 0;
    let match;
    while ((match = VARIABLE_RE.exec(body || "")) !== null) {
        found.add(Number(match[1]));
    }
    return Array.from(found).sort((a, b) => a - b);
};

const isApproved = (tpl) => (tpl?.status || "").toUpperCase() === "APPROVED";
const isMarketing = (tpl) => (tpl?.category || "").toUpperCase() === "MARKETING";

const CampaignCreate = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();

    const branchID = useSelector((s) => s.branchReducer.info?.id);
    const templates = useSelector((s) => s.campaignReducer.templates);
    const templatesLoading = useSelector((s) => s.campaignReducer.templatesStatus === PENDING);

    const [selectedName, setSelectedName] = useState("");
    const [variables, setVariables] = useState({});
    const [returnWindow, setReturnWindow] = useState(14);
    const [from, setFrom] = useState("10:00");
    const [to, setTo] = useState("18:00");
    const [submitting, setSubmitting] = useState(false);

    const [estimate, setEstimate] = useState(null);
    const [estimateLoading, setEstimateLoading] = useState(false);
    const [priceAck, setPriceAck] = useState(false);

    const [showCreate, setShowCreate] = useState(false);
    const [tplName, setTplName] = useState("");
    const [tplLanguage, setTplLanguage] = useState("ru");
    const [tplBody, setTplBody] = useState("");
    const [tplVariables, setTplVariables] = useState([]);
    const [creatingTpl, setCreatingTpl] = useState(false);

    useEffect(() => {
        if (branchID) dispatch(fetchCampaignTemplates(branchID));
    }, [dispatch, branchID]);

    const approvedTemplates = useMemo(
        () => (templates || []).filter((tpl) => isApproved(tpl) && isMarketing(tpl)),
        [templates],
    );

    const reviewTemplates = useMemo(
        () => (templates || []).filter((tpl) => !isApproved(tpl)),
        [templates],
    );

    const statusLabel = (status) => {
        const s = (status || "").toUpperCase();
        if (s === "APPROVED") return t("NSCampaigns.statusApproved");
        if (s === "REJECTED") return t("NSCampaigns.statusRejected");
        return t("NSCampaigns.statusPending");
    };

    const selectedTemplate = useMemo(
        () => approvedTemplates.find((tpl) => tpl.name === selectedName) || null,
        [approvedTemplates, selectedName],
    );

    const variableIndices = useMemo(
        () => (selectedTemplate ? parseVariableIndices(selectedTemplate.body) : []),
        [selectedTemplate],
    );

    useEffect(() => {
        setPriceAck(false);
        if (!branchID || !selectedTemplate) {
            setEstimate(null);
            return;
        }
        let cancelled = false;
        setEstimateLoading(true);
        campaignApi
            .estimate({ branch_id: branchID, category: selectedTemplate.category })
            .then((data) => {
                if (!cancelled) setEstimate(data);
            })
            .catch(() => {
                if (!cancelled) setEstimate(null);
            })
            .finally(() => {
                if (!cancelled) setEstimateLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [branchID, selectedTemplate]);

    const notify = (type, title, message) =>
        store.addNotification({
            title,
            message,
            type,
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: { duration: 5000, onScreen: true },
        });

    const onSelectTemplate = (name) => {
        setSelectedName(name);
        setVariables({});
    };

    const submitTemplate = async () => {
        if (!tplName.trim() || !tplBody.trim()) return;
        setCreatingTpl(true);
        try {
            await dispatch(
                createCampaignTemplate(branchID, {
                    name: tplName.trim(),
                    language: tplLanguage,
                    category: "MARKETING",
                    body: tplBody.trim(),
                    variables: tplVariables.map((v) => v.trim()).filter(Boolean),
                }),
            );
            notify("success", t("NSCampaigns.templateSubmittedTitle"), t("NSCampaigns.templateSubmitted"));
            setShowCreate(false);
            setTplName("");
            setTplBody("");
            setTplVariables([]);
            dispatch(fetchCampaignTemplates(branchID));
        } catch (e) {
            const msg = e?.response?.data?.error || t("NSCampaigns.templateSubmitError");
            notify("danger", t("NSCampaigns.templateSubmitError"), msg);
        } finally {
            setCreatingTpl(false);
        }
    };

    const allVariablesFilled = variableIndices.every((i) => (variables[i] || "").trim());
    const estimateReady = !estimateLoading && !!estimate;
    const estimateConfigured = estimateReady && estimate.configured === true;
    const canSubmit =
        !!selectedTemplate &&
        allVariablesFilled &&
        !!from &&
        !!to &&
        !submitting &&
        estimateConfigured &&
        priceAck;

    const submit = async () => {
        if (!canSubmit) return;
        const templateParams = variableIndices.map((i) => (variables[i] || "").trim());
        const req = {
            branch_id: branchID,
            channel: "channel_1",
            bucket: "marketing",
            event_type: "marketing.campaign",
            category: selectedTemplate.category,
            price_acknowledged: true,
            message: {
                type: "template",
                template_name: selectedTemplate.name,
                template_params: templateParams,
            },
            return_window_days: returnWindow,
            schedule: { mode: "window", from, to, tz: "Asia/Almaty" },
        };
        setSubmitting(true);
        try {
            const created = await dispatch(createCampaign(req));
            if (created?._id) history.push(`/campaigns/${created._id}`);
        } catch (e) {
            const code = e?.response?.data?.code;
            const priceMessages = {
                price_not_configured: "NSCampaigns.priceBlockedNotConfigured",
                price_not_acknowledged: "NSCampaigns.priceBlockedNotAcknowledged",
                estimate_failed: "NSCampaigns.priceBlockedEstimateFailed",
            };
            if (priceMessages[code]) {
                notify("danger", t("NSCampaigns.createError"), t(priceMessages[code]));
            } else {
                const msg = e?.response?.data?.error || t("NSCampaigns.createError");
                notify("danger", t("NSCampaigns.createError"), msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.container}>
            <PageHeader title={t("NSCampaigns.createButton")} />

            <p className={styles.infoNote}>{t("NSCampaigns.wabaInfoNote")}</p>

            <div className={styles.field}>
                <label className={styles.label}>{t("NSCampaigns.templateSection")}</label>
                {templatesLoading ? (
                    <p className={styles.loading}>{t("NSCampaigns.templatesLoading")}</p>
                ) : approvedTemplates.length > 0 ? (
                    <select
                        className={styles.input}
                        value={selectedName}
                        onChange={(e) => onSelectTemplate(e.target.value)}
                    >
                        <option value="">{t("NSCampaigns.selectTemplate")}</option>
                        {approvedTemplates.map((tpl) => (
                            <option key={tpl.name} value={tpl.name}>
                                {tpl.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <p className={styles.windowHint}>{t("NSCampaigns.noApprovedTemplates")}</p>
                )}
                {!templatesLoading && reviewTemplates.length > 0 && (
                    <div className={styles.scheduleCard}>
                        <label className={styles.label}>{t("NSCampaigns.templatesInReview")}</label>
                        {reviewTemplates.map((tpl) => (
                            <p key={tpl.name} className={styles.estimateLine}>
                                <span>{tpl.name}</span>
                                <span className={styles.estimateDot}>·</span>
                                <span>{statusLabel(tpl.status)}</span>
                            </p>
                        ))}
                    </div>
                )}
                {selectedTemplate && (
                    <div className={styles.scheduleCard}>
                        <label className={styles.label}>{t("NSCampaigns.selectedTemplatePreview")}</label>
                        <p>{selectedTemplate.body}</p>
                    </div>
                )}
            </div>

            {!showCreate && (
                <div className={styles.actions}>
                    <button className={styles.backBtn} onClick={() => setShowCreate(true)}>
                        {t("NSCampaigns.createNewTemplate")}
                    </button>
                </div>
            )}

            {showCreate && (
                <div className={styles.scheduleCard}>
                    <div className={styles.field}>
                        <label className={styles.label}>{t("NSCampaigns.templateName")}</label>
                        <input
                            className={styles.input}
                            value={tplName}
                            onChange={(e) => setTplName(e.target.value)}
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>{t("NSCampaigns.templateLanguage")}</label>
                        <select
                            className={styles.input}
                            value={tplLanguage}
                            onChange={(e) => setTplLanguage(e.target.value)}
                        >
                            <option value="ru">RU</option>
                            <option value="en">EN</option>
                        </select>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>{t("NSCampaigns.templateBodyLabel")}</label>
                        <textarea
                            className={styles.textarea}
                            rows={5}
                            value={tplBody}
                            onChange={(e) => setTplBody(e.target.value)}
                        />
                        <span className={styles.windowHint}>
                            {t("NSCampaigns.templateBodyHint", { code1: "{{1}}", code2: "{{2}}" })}
                        </span>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>{t("NSCampaigns.templateVariables")}</label>
                        {tplVariables.map((value, idx) => (
                            <div key={idx} className={styles.windowRow}>
                                <input
                                    className={styles.input}
                                    value={value}
                                    onChange={(e) =>
                                        setTplVariables((prev) =>
                                            prev.map((v, i) => (i === idx ? e.target.value : v)),
                                        )
                                    }
                                />
                                <button
                                    className={styles.backBtn}
                                    onClick={() =>
                                        setTplVariables((prev) => prev.filter((_, i) => i !== idx))
                                    }
                                >
                                    {t("NSCampaigns.removeVariable")}
                                </button>
                            </div>
                        ))}
                        <div className={styles.actions}>
                            <button
                                className={styles.backBtn}
                                onClick={() => setTplVariables((prev) => [...prev, ""])}
                            >
                                {t("NSCampaigns.addVariable")}
                            </button>
                        </div>
                    </div>
                    <div className={styles.actions}>
                        <ButtonFilled
                            onClick={submitTemplate}
                            loading={creatingTpl}
                            disabled={!tplName.trim() || !tplBody.trim() || creatingTpl}
                        >
                            {t("NSCampaigns.submitTemplate")}
                        </ButtonFilled>
                        <button className={styles.backBtn} onClick={() => setShowCreate(false)}>
                            {t("NSCampaigns.cancel")}
                        </button>
                    </div>
                </div>
            )}

            {selectedTemplate && variableIndices.length > 0 && (
                <div className={styles.field}>
                    <label className={styles.label}>{t("NSCampaigns.variablesSection")}</label>
                    {variableIndices.map((i) => (
                        <div key={i} className={styles.field}>
                            <label className={styles.label}>
                                {t("NSCampaigns.variableLabel", { index: i })}
                            </label>
                            <input
                                className={styles.input}
                                value={variables[i] || ""}
                                onChange={(e) =>
                                    setVariables((prev) => ({ ...prev, [i]: e.target.value }))
                                }
                            />
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.field}>
                <label className={styles.label}>{t("NSCampaigns.returnWindowSection")}</label>
                <select
                    className={styles.input}
                    value={returnWindow}
                    onChange={(e) => setReturnWindow(Number(e.target.value))}
                >
                    {RETURN_WINDOW_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                            {d} {t("NSCampaigns.daysShort")}
                        </option>
                    ))}
                </select>
                <p className={styles.windowHint}>{t("NSCampaigns.returnWindowHint")}</p>
            </div>

            <div className={styles.field}>
                <label className={styles.label}>{t("NSCampaigns.sendWindowSection")}</label>
                <div className={styles.windowRow}>
                    <div className={styles.timeField}>
                        <label className={styles.label}>{t("NSCampaigns.windowFrom")}</label>
                        <input
                            className={styles.input}
                            type="time"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                        />
                    </div>
                    <div className={styles.timeField}>
                        <label className={styles.label}>{t("NSCampaigns.windowTo")}</label>
                        <input
                            className={styles.input}
                            type="time"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                        />
                    </div>
                </div>
                <p className={styles.windowHint}>{t("NSCampaigns.windowRequiredHint")}</p>
            </div>

            {selectedTemplate && (
                <div className={styles.estimateBlock}>
                    <label className={styles.label}>{t("NSCampaigns.estimateSection")}</label>
                    {estimateLoading ? (
                        <p className={styles.loading}>…</p>
                    ) : estimate ? (
                        <>
                            <p className={styles.estimateLine}>
                                <span>{t("NSCampaigns.estimateRecipients", { count: estimate.recipients })}</span>
                                <span className={styles.estimateDot}>·</span>
                                <span>{t("NSCampaigns.estimateCost", { total: estimate.total, currency: estimate.currency })}</span>
                            </p>
                            {estimate.configured ? (
                                <>
                                    <label className={styles.priceAck}>
                                        <input
                                            type="checkbox"
                                            checked={priceAck}
                                            onChange={(e) => setPriceAck(e.target.checked)}
                                        />
                                        <span>{t("NSCampaigns.priceConfirmCheckbox")}</span>
                                    </label>
                                    {!priceAck && (
                                        <p className={styles.windowHint}>
                                            {t("NSCampaigns.priceBlockedNotAcknowledged")}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className={styles.windowHint}>{t("NSCampaigns.priceBlockedNotConfigured")}</p>
                            )}
                        </>
                    ) : (
                        <p className={styles.windowHint}>{t("NSCampaigns.priceBlockedEstimateFailed")}</p>
                    )}
                </div>
            )}

            <div className={styles.actions}>
                <ButtonFilled onClick={submit} loading={submitting} disabled={!canSubmit}>
                    {t("NSCampaigns.createButton")}
                </ButtonFilled>
            </div>
        </div>
    );
};

export default CampaignCreate;
